import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Input } from "@/components/ui/input";
import { Sentry } from "@/lib/sentry";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import type { AreaTree, AreaTreeNode } from "@/types/area";

/** One pickable area or part: `name` is the label the list shows, `area` the node it stands for. */
interface AreaOption {
  id: string;
  name: string;
  /** Absent for a value the city's tree does not hold. */
  area?: AreaTreeNode;
}

export const NO_COUNTRY_MESSAGE = "Fix the venue's city first";

const label = (node: AreaTreeNode, partName?: string): string =>
  partName ? `${node.name} — ${partName}` : node.name;

/**
 * The tree as options. With `onlyPickable` it is what the list offers: areas with their part after the name, unparented
 * areas by name, and a part only when venues sit on it directly (labelled as the whole part): a part with none of its own
 * is a heading, not a place to put a venue. Without it, every node, labelled as it shows when it is the current value.
 */
function buildOptions(tree: AreaTree, onlyPickable: boolean): AreaOption[] {
  const options: AreaOption[] = [];
  const add = (node: AreaTreeNode, name: string) =>
    options.push({ id: node.id, name, area: node });
  for (const { part, children } of tree.parts) {
    if (!onlyPickable) add(part, label(part));
    else if (part.venueCount > 0) add(part, `${part.name} (whole part)`);
    for (const child of children) add(child, label(child, part.name));
  }
  for (const node of tree.unparented) add(node, label(node));
  return options;
}

interface AreaPickerProps {
  /** The venue's city (`geodata.name`) and country; the areas are the city's. */
  city?: string;
  countryCode?: string;
  /** The area picked now: its id, or its official name when `valueBy` is `"name"`. */
  value: string | null | undefined;
  /** An area picked, or `""` when it is cleared. Carries the area's id, or its name when `valueBy` is `"name"`. */
  onChange: (value: string) => void;
  /** `"name"` for a form that stores the area as text (the enrichment write takes the name). */
  valueBy?: "id" | "name";
  /** The Places suggestion, e.g. `Γκάζι, Athens`: each part is resolved in order and the first hit is picked. */
  hint?: string;
  /** The label to show for a `value` the city's tree does not hold. */
  currentName?: string | null;
  /** A city with no areas: the venue's area is typed, as before areas existed. */
  textValue?: string | null;
  onTextChange?: (text: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * One of the venue's city's official areas (or a part), from the areas tree. The server writes the venue's `area` text
 * from the area picked, so a venue is pointed at an area, never given a spelling.
 */
export function AreaPicker({
  city,
  countryCode,
  value,
  onChange,
  valueBy = "id",
  hint,
  currentName,
  textValue,
  onTextChange,
  disabled = false,
  ariaLabel = "Area",
}: AreaPickerProps) {
  const { tree: loadTree, resolve } = useAreaRepository();
  const labelId = `${useId()}-label`;
  const [tree, setTree] = useState<AreaTree | null>(null);
  const [failed, setFailed] = useState(false);
  const [hintMiss, setHintMiss] = useState(false);
  const loading = useRef<Promise<AreaTree | null>>(Promise.resolve(null));
  const usable = Boolean(city && countryCode);

  // RemoteSelect keeps the `onValueChanged` it first rendered with; the ref always holds the latest handler.
  const latest = useRef({ onChange, valueBy });
  useEffect(() => {
    latest.current = { onChange, valueBy };
  });

  useEffect(() => {
    setTree(null);
    setFailed(false);
    if (!city || !countryCode) return;
    let live = true;
    const request = loadTree(city, countryCode)
      .then((result) => {
        if (live) setTree(result);
        return result;
      })
      .catch((error) => {
        Sentry.captureException(error);
        if (live) setFailed(true);
        return null;
      });
    loading.current = request;
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, countryCode]);

  const everyOption = useMemo(
    () => (tree ? buildOptions(tree, false) : []),
    [tree],
  );
  const selected = useMemo((): AreaOption | undefined => {
    if (!value) return undefined;
    const found = everyOption.find((option) =>
      valueBy === "name"
        ? option.area?.name.toLowerCase() === value.toLowerCase()
        : option.id === value,
    );
    if (found) return found;
    // Not in the city's tree (or the tree is still loading): shown as it is, so the current value is never hidden.
    const fallback = valueBy === "name" ? value : (currentName ?? value);
    return { id: value, name: fallback };
  }, [everyOption, value, valueBy, currentName]);

  // The Places suggestion, for a venue with no area yet. The tree has to be the current city's: an area picked for the
  // city before a correction is not in it, so it is cleared and the suggestion is resolved again for the new city.
  useEffect(() => {
    setHintMiss(false);
    if (!hint || !city || !countryCode || !tree) return;
    if (value) {
      if (
        latest.current.valueBy === "id" &&
        !everyOption.some((option) => option.id === value)
      ) {
        latest.current.onChange("");
      }
      return;
    }
    let live = true;
    void (async () => {
      for (const part of hint.split(",")) {
        const name = part.trim();
        if (!name) continue;
        try {
          const area = await resolve(city, countryCode, name);
          if (!live) return;
          if (area) {
            latest.current.onChange(
              latest.current.valueBy === "name" ? area.name : area.id,
            );
            return;
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      }
      if (live) setHintMiss(true);
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hint, city, countryCode, tree, value]);

  if (!usable) {
    return (
      <div className="space-y-1">
        <RemoteSelect<AreaOption>
          disabled
          value={undefined}
          onValueChanged={() => {}}
          accessorFn={(option) => ({
            id: option?.id ?? "",
            name: option?.name ?? "",
          })}
          onSearch={async () => []}
          ariaLabelledBy={labelId}
          className="w-full font-normal"
        />
        <span id={labelId} className="sr-only">
          {ariaLabel}
        </span>
        <p className="text-xs text-muted-foreground">{NO_COUNTRY_MESSAGE}</p>
      </div>
    );
  }

  const noAreas =
    tree !== null && tree.parts.length === 0 && tree.unparented.length === 0;
  if (noAreas) {
    return (
      <div className="space-y-1">
        <Input
          aria-label={ariaLabel}
          value={textValue ?? (valueBy === "name" ? (value ?? "") : "")}
          disabled={disabled}
          onChange={(event) =>
            (onTextChange ?? latest.current.onChange)(event.target.value)
          }
        />
        <p className="text-xs text-muted-foreground">
          No areas for {city} yet, so the area is typed.
        </p>
      </div>
    );
  }

  const pick = (option: AreaOption | undefined) => {
    const { onChange: emit, valueBy: by } = latest.current;
    emit(
      option
        ? by === "name"
          ? (option.area?.name ?? option.name)
          : option.id
        : "",
    );
  };

  const areaId = selected?.area || valueBy === "id" ? selected?.id : undefined;
  return (
    <div className="space-y-1 min-w-0">
      <span id={labelId} className="sr-only">
        {ariaLabel}
      </span>
      <RemoteSelect<AreaOption>
        value={selected}
        showSelected
        loadOnOpen
        // A venue with text but no linked area: the curator sees what was there.
        placeholder={!hint && currentName ? currentName : undefined}
        triggerType="change"
        disabled={disabled}
        ariaLabelledBy={labelId}
        className="w-full font-normal"
        onValueChanged={pick}
        accessorFn={(option) => ({
          id: option?.id ?? "",
          name: option?.name ?? "",
        })}
        onSearch={async (query, onStatusChanged) => {
          onStatusChanged("loading");
          const loaded = tree ?? (await loading.current);
          if (!loaded) {
            onStatusChanged("error");
            return [];
          }
          onStatusChanged("success");
          const needle = query.trim().toLowerCase();
          return buildOptions(loaded, true).filter(
            (option) =>
              option.id !== selected?.id &&
              option.name.toLowerCase().includes(needle),
          );
        }}
      />
      {failed && (
        <p className="text-xs text-destructive">Could not load the areas.</p>
      )}
      {hint && (
        <p className="text-xs text-muted-foreground">
          Google Places suggested: {hint}
          {hintMiss && " (no matching area)"}
        </p>
      )}
      {areaId && (
        <Link
          to={`/areas/edit/${encodeURIComponent(areaId)}`}
          className="text-xs underline text-muted-foreground hover:text-foreground"
        >
          Open area
        </Link>
      )}
    </div>
  );
}
